// src/components/admin/testSeries/MainsTestViewManager.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";

// ✅ Use environment variable or fallback
const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const MainsTestViewManager = () => {
  const [testsGrouped, setTestsGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  const [expandedDays, setExpandedDays] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [accessFilter, setAccessFilter] = useState("all");

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token;
      const res = await axios.get(`${API_BASE}/api/admin/test-series/mains`, {
        headers: { Authorization: `Bearer ${token}` },
      });      

      const grouped = {};
      const tests = Array.isArray(res.data) ? res.data : res.data.tests || [];

      tests.forEach((test) => {
        const date = dayjs(test.date);
        const year = date.year();
        const month = date.format("MMMM");
        const day = date.date();

        if (!grouped[year]) grouped[year] = {};
        if (!grouped[year][month]) grouped[year][month] = {};
        if (!grouped[year][month][day]) grouped[year][month][day] = [];

        grouped[year][month][day].push(test);
      });

      setTestsGrouped(grouped);
    } catch (err) {
      console.error("Failed to fetch Mains tests", err);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (type, key) => {
    if (type === "year")
      setExpandedYears((prev) => ({ ...prev, [key]: !prev[key] }));
    if (type === "month")
      setExpandedMonths((prev) => ({ ...prev, [key]: !prev[key] }));
    if (type === "day")
      setExpandedDays((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const deleteTest = async (id) => {
    if (!window.confirm("Are you sure you want to delete this test?")) return;
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token;
      await axios.delete(`${API_BASE}/api/admin/test-series/mains/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchTests();
    } catch (err) {
      console.error("Error deleting test", err);
    }
  };

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">
        📋 View Mains Tests
      </h2>
      <div className="flex flex-wrap gap-4 mb-6">

  {/* Publication Filter */}
  <div>
    <label className="block text-sm font-medium mb-1">
      Publication
    </label>

    <select
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      className="border rounded p-2"
    >
      <option value="all">All</option>
      <option value="published">Published</option>
      <option value="draft">Draft</option>
    </select>
  </div>

  {/* Access Filter */}

  <div>
    <label className="block text-sm font-medium mb-1">
      Access
    </label>

    <select
      value={accessFilter}
      onChange={(e) => setAccessFilter(e.target.value)}
      className="border rounded p-2"
    >
      <option value="all">All</option>
      <option value="free">Free</option>
      <option value="premium">Premium</option>
    </select>
  </div>

</div>

      {loading ? (
        <p className="text-gray-700 dark:text-gray-300">Loading...</p>
      ) : (
        Object.entries(testsGrouped).map(([year, months]) => (
          <div key={year} className="mb-6">
            <button
              onClick={() => toggle("year", year)}
              className="text-xl font-bold text-blue-700 dark:text-blue-400 mb-2 hover:underline"
            >
              {expandedYears[year] ? "▼" : "►"} {year}
            </button>

            {expandedYears[year] &&
              Object.entries(months).map(([month, days]) => {
                const monthKey = `${year}-${month}`;
                return (
                  <div key={month} className="ml-4 mb-4">
                    <button
                      onClick={() => toggle("month", monthKey)}
                      className="text-lg font-semibold text-blue-500 dark:text-blue-300 mb-1 hover:underline"
                    >
                      {expandedMonths[monthKey] ? "▼" : "►"} {month}
                    </button>

                    {expandedMonths[monthKey] &&
                      Object.entries(days).map(([day, tests]) => {
                        const dayKey = `${year}-${month}-${day}`;
                        return (
                          <div key={day} className="ml-6 mb-2">
                            <button
                              onClick={() => toggle("day", dayKey)}
                              className="font-medium text-gray-700 dark:text-gray-300 mb-2 hover:underline"
                            >
                              {expandedDays[dayKey] ? "▼" : "►"} Day {day}
                            </button>

                            {expandedDays[dayKey] &&
                              tests
  .filter((test) => {

    const statusMatch =
      statusFilter === "all"
        ? true
        : statusFilter === "published"
        ? test.published
        : !test.published;

    const accessMatch =
      accessFilter === "all"
        ? true
        : accessFilter === "free"
        ? test.isFree
        : !test.isFree;

    return statusMatch && accessMatch;

  })
  .map((test) => (
                                <div
                                  key={test._id}
                                  className="border p-4 rounded-lg mb-3 shadow hover:shadow-lg bg-white dark:bg-gray-800 transition"
                                >
                                  <div className="flex justify-between items-center">
  <h6 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
    {test.title}
  </h6>

  <div className="flex gap-2">

    <span
      className={`text-xs px-2 py-1 rounded-full ${
        test.published
          ? "bg-blue-100 text-blue-700"
          : "bg-gray-200 text-gray-700"
      }`}
    >
      {test.published ? "Published" : "Draft"}
    </span>

    <span
      className={`text-xs px-2 py-1 rounded-full ${
        test.isFree
          ? "bg-green-100 text-green-700"
          : "bg-yellow-100 text-yellow-700"
      }`}
    >
      {test.isFree ? "Free" : "Premium"}
    </span>

  </div>
</div>
                                  <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">
                                    <strong>Section:</strong>{" "}
                                    {test.section || "—"} |{" "}
                                    <strong>Subject:</strong>{" "}
                                    {test.subject || "—"}
                                  </p>
                                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                                    Uploaded by: {test.uploadedBy?.name || "—"}{" "}
                                    ({test.uploadedBy?.email || "—"})
                                  </p>

                                  <ul className="list-disc list-inside text-sm mb-2">
                                    {test.questions.map((q, i) => (
                                      <li
                                        key={i}
                                        className="mb-2 text-gray-800 dark:text-gray-200"
                                      >
                                        <strong>Q{i + 1}:</strong> {q.question}
                                        <br />
                                        <span className="text-green-700 dark:text-green-400 block mt-1">
                                          <strong>Model Answer:</strong>{" "}
                                          {q.modelAnswer || "—"}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>

                                  <div className="flex gap-3 mt-2">
                                    <button
                                      onClick={() => setSelectedTest(test)}
                                      className="px-3 py-1 text-sm bg-yellow-500 dark:bg-yellow-600 text-white rounded hover:brightness-90 transition"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => deleteTest(test._id)}
                                      className="px-3 py-1 text-sm bg-red-600 dark:bg-red-700 text-white rounded hover:brightness-90 transition"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              ))}
                          </div>
                        );
                      })}
                  </div>
                );
              })}
          </div>
        ))
      )}

      {selectedTest && (
        <EditModal
          test={selectedTest}
          onClose={() => setSelectedTest(null)}
          onUpdate={fetchTests}
        />
      )}
    </div>
  );
};

// =======================
// ✅ Edit Modal with Dark Mode
// =======================
const EditModal = ({ test, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
  title: test.title,
  section: test.section || "",
  subject: test.subject || "",
  status: test.published ? "published" : "draft",
  isFree: test.isFree ?? true,
  questions: test.questions.map((q) => ({ ...q })),
});
  const [saving, setSaving] = useState(false);

  const handleChange = (index, field, value) => {
    const updated = [...formData.questions];
    updated[index][field] = value;
    setFormData({ ...formData, questions: updated });
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [...formData.questions, { question: "", modelAnswer: "" }],
    });
  };

  const removeQuestion = (index) => {
    const updated = [...formData.questions];
    updated.splice(index, 1);
    setFormData({ ...formData, questions: updated });
  };

  const handleSubmit = async () => {
    if (
      !formData.title.trim() ||
      !formData.section.trim() ||
      !formData.subject.trim()
    ) {
      alert("Please fill all fields.");
      return;
    }

    setSaving(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token;
      await axios.put(
        `${API_BASE}/api/admin/test-series/mains/${test._id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      onUpdate();
      onClose();
    } catch (err) {
      console.error("Failed to update test", err);
      alert("Update failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center z-50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 w-full max-w-3xl mt-10 rounded shadow-lg p-6">
        <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">
          Edit Mains Test
        </h2>

        <label className="block mb-3 text-gray-900 dark:text-gray-100">
          <span className="font-semibold">Title:</span>
          <input
            type="text"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            className="w-full mt-1 border p-2 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
          />
        </label>

        <label className="block mb-3 text-gray-900 dark:text-gray-100">
          <span className="font-semibold">Section:</span>
          <select
            value={formData.section}
            onChange={(e) =>
              setFormData({ ...formData, section: e.target.value })
            }
            className="w-full mt-1 border p-2 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
          >
            <option value="">-- Select Section --</option>
            <option value="GS-1">GS-1</option>
            <option value="GS-2">GS-2</option>
            <option value="GS-3">GS-3</option>
            <option value="GS-4">GS-4</option>
            <option value="Ethics">Ethics</option>
            <option value="Extra">Extra</option>
          </select>
        </label>

        <label className="block mb-3 text-gray-900 dark:text-gray-100">
  <span className="font-semibold">Status:</span>

  <select
    value={formData.status}
    onChange={(e) =>
      setFormData({
        ...formData,
        status: e.target.value,
      })
    }
    className="w-full mt-1 border p-2 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
  >
    <option value="draft">Draft</option>
    <option value="published">Publish Now</option>
  </select>
</label>

<label className="block mb-3 text-gray-900 dark:text-gray-100">
  <span className="font-semibold">Status:</span>

  <select
    value={formData.status}
    onChange={(e) =>
      setFormData({
        ...formData,
        status: e.target.value,
      })
    }
    className="w-full mt-1 border p-2 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
  >
    <option value="draft">Draft</option>
    <option value="published">Published</option>
  </select>
</label>

        <label className="block mb-3 text-gray-900 dark:text-gray-100">
          <span className="font-semibold">Subject:</span>
          <label className="block mb-3 text-gray-900 dark:text-gray-100">
  <span className="font-semibold">Access:</span>
  <select
    value={formData.isFree}
    onChange={(e) =>
      setFormData({
        ...formData,
        isFree: e.target.value === "true",
      })
    }
    className="w-full mt-1 border p-2 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
  >
    <option value="true">Free</option>
    <option value="false">Premium</option>
  </select>
</label>
          <input
            type="text"
            value={formData.subject}
            onChange={(e) =>
              setFormData({ ...formData, subject: e.target.value })
            }
            placeholder="e.g., Geography, Polity, Disaster Management"
            className="w-full mt-1 border p-2 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
          />
        </label>

        {formData.questions.map((q, idx) => (
          <div
            key={idx}
            className="border p-3 rounded mb-3 bg-gray-50 dark:bg-gray-800"
          >
            <label className="block mb-2 text-gray-900 dark:text-gray-100">
              <span className="font-semibold">Question {idx + 1}:</span>
              <textarea
                value={q.question}
                onChange={(e) => handleChange(idx, "question", e.target.value)}
                className="w-full mt-1 border p-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                rows={2}
              />
            </label>

            <label className="block mb-2 text-gray-900 dark:text-gray-100">
              <span className="font-semibold">Model Answer:</span>
              <textarea
                value={q.modelAnswer}
                onChange={(e) =>
                  handleChange(idx, "modelAnswer", e.target.value)
                }
                className="w-full mt-1 border p-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                rows={3}
              />
            </label>

            <button
              onClick={() => removeQuestion(idx)}
              className="text-red-500 text-sm mt-1 hover:underline"
            >
              Remove Question
            </button>
          </div>
        ))}

        <div className="flex items-center gap-4 mt-4">
          <button
            onClick={addQuestion}
            className="bg-blue-500 dark:bg-blue-600 text-white px-4 py-1 rounded hover:brightness-90 transition"
          >
            + Add Question
          </button>
          <button
            onClick={handleSubmit}
            className="bg-green-600 dark:bg-green-700 text-white px-4 py-1 rounded hover:brightness-90 transition"
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
          <button
            onClick={onClose}
            className="ml-auto text-gray-700 dark:text-gray-300 underline hover:text-gray-900 dark:hover:text-gray-100"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MainsTestViewManager;
